import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Learn() {
  const [entries, setEntries] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [letter, setLetter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/learn/topics").then((r) => setTopics(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (topic) params.topic = topic;
    if (letter) params.letter = letter;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/learn", { params }).then((r) => setEntries(r.data)).finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [topic, letter, search]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <SEO title="Learn Library — Crystals, Modalities, Chakras & More" description="A calm knowledge hub: crystals, Reiki, sound healing, chakras, moon phases and wellness basics — written plainly and linked to real Calgary practitioners." />
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">The Learn Library</p>
      <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-2">Knowledge, gently kept</h1>
      <p className="text-muted-foreground text-sm mb-10 max-w-xl">Crystals, modalities, chakras, moon phases, and wellness basics — written plainly, linked to real practitioners.</p>

      <div className="relative max-w-md mb-6">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="learn-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the library…" className="pl-10 rounded-full bg-card/70" />
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="learn-topic-filters">
        <Badge onClick={() => setTopic("")} variant={!topic ? "default" : "secondary"} className="cursor-pointer px-4 py-1.5 rounded-full font-normal">All topics</Badge>
        {topics.map((t) => (
          <Badge key={t} data-testid={`learn-topic-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTopic(t === topic ? "" : t)}
            variant={topic === t ? "default" : "secondary"} className="cursor-pointer px-4 py-1.5 rounded-full font-normal">{t}</Badge>
        ))}
      </div>

      <div className="flex flex-wrap gap-1 mb-10 text-xs" data-testid="learn-alpha-index">
        <button onClick={() => setLetter("")} className={`w-7 h-7 rounded ${!letter ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>All</button>
        {LETTERS.map((l) => (
          <button key={l} onClick={() => setLetter(l === letter ? "" : l)}
            className={`w-7 h-7 rounded ${letter === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground py-16 text-center">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground py-16 text-center" data-testid="learn-empty-state">Nothing found here yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="learn-results">
          {entries.map((e) => (
            <Link key={e.id} to={`/learn/${e.slug}`} data-testid={`learn-card-${e.slug}`} className="silk-card rounded-lg overflow-hidden block group">
              {e.image && (
                <div className="overflow-hidden h-36">
                  <img src={assetUrl(e.image)} alt={e.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
              )}
              <div className="p-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{e.topic}</p>
                <h3 className="font-display text-2xl text-foreground mb-2">{e.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{e.summary}</p>
                <p className="cream-link text-xs mt-4 flex items-center gap-1">Read <ArrowRight size={12} /></p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
